import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Title, useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { fetchConversations, syncFromTwilio } from './messageInboxApi';
import { MessageInboxConversation } from '../types/messageInbox';
import useAuthorize from '../hooks/useAuthorize';
import { WHATSAPP_MODULE_ROLES } from '../roles/portalRoles';

const MessageInboxList = () => {
  useAuthorize(WHATSAPP_MODULE_ROLES);
  const notify = useNotify();
  const navigate = useNavigate();
  const [rows, setRows] = useState<MessageInboxConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [needsReply, setNeedsReply] = useState('true');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const perPage = 25;

  const load = async () => {
    setLoading(true);
    try {
      const result = await fetchConversations({
        _start: page * perPage,
        _end: (page + 1) * perPage,
        q: search || undefined,
        needs_reply: needsReply || undefined,
      } as Record<string, string | number>);
      setRows(result.data);
      setTotal(result.total);
    } catch (error) {
      console.error(error);
      notify('Failed to load message inbox', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [page, needsReply]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const stats = await syncFromTwilio();
      notify(
        `Synced ${stats.inboxUpserted} inbox messages from Twilio (${stats.fetched} fetched)`,
        { type: 'success' }
      );
      setPage(0);
      await load();
    } catch (error) {
      console.error(error);
      notify('Failed to sync from Twilio', { type: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Title title="Message Inbox" />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Twilio WhatsApp replies and manual responses. Use Sync from Twilio to import recent messages, or configure webhooks for live updates.
      </Typography>

      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          label="Search phone, name, message"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 280 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Reply status</InputLabel>
          <Select
            label="Reply status"
            value={needsReply}
            onChange={(e) => setNeedsReply(e.target.value)}
          >
            <MenuItem value="true">Needs reply</MenuItem>
            <MenuItem value="false">Replied</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" onClick={() => { setPage(0); load(); }}>
          Apply filters
        </Button>
        <Button variant="outlined" onClick={handleSync} disabled={syncing}>
          {syncing ? 'Syncing...' : 'Sync from Twilio'}
        </Button>
      </Paper>

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Contact</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Last message</TableCell>
              <TableCell>Direction</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Needs reply</TableCell>
              <TableCell>Updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7}>Loading...</TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>No conversations yet. Configure Twilio webhook to start receiving replies.</TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={row.conversation_key}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/messageinbox/${row.conversation_key}/show`)}
                >
                  <TableCell>
                    <Typography variant="body2">{row.contact_name || 'Unknown'}</Typography>
                    <Typography variant="caption" color="text.secondary">{row.contact_phone}</Typography>
                  </TableCell>
                  <TableCell>
                    {row.customer_id ? (
                      <>
                        <Typography variant="body2">
                          {row.customer_name || `#${row.customer_id}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.company_name || '-'}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="caption" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 420, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {row.last_message}
                  </TableCell>
                  <TableCell>{row.last_direction}</TableCell>
                  <TableCell>{row.last_status}</TableCell>
                  <TableCell>
                    {row.needs_reply ? <Chip label="Needs reply" color="warning" size="small" /> : <Chip label="Replied" size="small" />}
                  </TableCell>
                  <TableCell>{row.last_message_at ? new Date(row.last_message_at).toLocaleString() : '-'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Typography variant="body2">{total} conversations</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</Button>
          <Button disabled={(page + 1) * perPage >= total} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </Box>
      </Box>
    </Box>
  );
};

export default MessageInboxList;
